import React, { FC, useEffect, useState } from 'react';

import { Flex } from 'Src/components/layout/Flex';
import { useCheckItemBuilder } from 'Src/feature/check-item/useCheckItemBuilder';
import { useLogger } from 'Src/feature/context-logger/useLogger';
import { Divider } from 'Src/feature/divider/Divider';
import { ModifierTreeItem } from 'Src/feature/modifier/useModifierTree';
import { ModifierCommandUI } from 'Src/feature/modifier-command/ModifierCommandUI';
import { ModelType } from 'Src/models/modelPath';

import { ModifierBreadCrumbs } from './ModifierBreadCrumbs';
import { ModifierCardButton } from './ModifierCardButton';

import styles from './ModifierCard.module.scss';

const sectionId = 'ModifierCard';

interface ModifierCardProps {
  modifierPath: ModifierTreeItem['path']['full'];
}
const ModifierCard: FC<ModifierCardProps> = ({
  modifierPath,
  ...rest
}) => {
  const log = useLogger().getSubLogger({ name: sectionId });

  const checkItemBuilder = useCheckItemBuilder();
  const modifiers = checkItemBuilder.modifiers;

  const isSplit = !!checkItemBuilder.source.checkItem?.splitBy && checkItemBuilder.source.checkItem?.splitBy > 0;

  let modifier = undefined as undefined | ModifierTreeItem;
  modifiers.tree.root?.walk('down', (item) => {
    if (item.path.full === modifierPath) {
      // deterministic from modifiers.tree.root to avoid stale state
      modifier = item;
      throw 'break';
    }
  });

  const modifierKey = modifier?.path.full;
  const [activeBreadCrumbId, activeBreadCrumbIdSet] = useState(modifierKey);

  // react only for changes for ModifierCardProps.modifier.path.full updates
  // - this should only happen when ModifierCard first initializes
  useEffect(() => {
    if (activeBreadCrumbId !== modifierKey) {
      // reset initialization state
      activeBreadCrumbIdSet(modifierKey);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modifierKey]);

  const onCardButtonClick = (next: ModifierTreeItem) => {
    const nextKey = next.path.full;
    // only can apply command palette logic to modifier(s)
    if (next.type === ModelType.modifier) {
      const { model } = next as ModifierTreeItem<ModelType.modifier>;
      // selection/validation logic must happen first
      // - determines what ui should show next based on selection state
      if (modifiers.commandPalette.selected.tap) {
        // build a user driven command palette entry for "tap"
        const modifierCommand = modifiers.commandPalette.selected.tap;
        const modifierCommandOption = model.options.find(({ cmdId }) => cmdId === modifierCommand.id);
        modifiers.onItemSelect(next, { command: new ModifierCommandUI({ model: modifierCommand, option: modifierCommandOption, intents: ['badge', 'tap'] }) });
        // toggle off modifier command palette selection
        modifiers.commandPalette.deselect();
      } else if (modifiers.commandPalette.selected.longPress) {
        // build a user driven command palette entry for "longPress"
        const modifierCommand = modifiers.commandPalette.selected.longPress;
        const modifierCommandOption = model.options.find(({ cmdId }) => cmdId === modifierCommand.id);
        modifiers.onItemSelect(next, { command: new ModifierCommandUI({ model: modifierCommand, option: modifierCommandOption, intents: ['badge', 'long-press'] }) });
      } else {
        modifiers.onItemSelect(next);
      }
    } else {
      modifiers.onItemSelect(next);
      activeBreadCrumbIdSet(nextKey);
    }

    if (modifiers.commandPalette.selected.longPress) {
      log.info('onCardButtonClick()', 'block nested navigation while longpress');
      // too much to parse at the moment
    } else {
      if (next.selections.has.any) {
        // rebuild nested modifier ui only if currently selected
        // rebuild modifier breadcrumbs when selecting a ModifierGroup
        // - walk up from "next" selection to append more bread crumbs
        // - omitting the main button modifier & the menu item root node
        if (next.type === ModelType.modifierGroup) {
          activeBreadCrumbIdSet(nextKey);
        }
      }
    }
  };

  let modifierChildren: ModifierTreeItem[] = [];

  modifiers.tree.root?.walk('down', (item) => {
    if (item.path.full === activeBreadCrumbId) {
      // rebuild modifier buttons underneath breadcrumbs
      // - deterministic from modifiers.tree.root to avoid stale state
      modifierChildren = item.children;
      throw 'break';
    }
  });

  const getPriceDifferential = (item: ModifierTreeItem): undefined | number => {
    // price differentials should only exist on modifier types
    if (item.type !== ModelType.modifier) return undefined;
    const { model } = item as ModifierTreeItem<ModelType.modifier>;
    // selected/active modifier command id from command palette
    let commandId = undefined as undefined | number;
    if (modifiers.commandPalette.selected.tap?.id) commandId = modifiers.commandPalette.selected.tap.id;
    else if (modifiers.commandPalette.selected.longPress?.id) commandId = modifiers.commandPalette.selected.longPress.id;
    else commandId = item.selections.last?.command?.model.id;

    // command palette links to the hard-coded db entities for ModifierCommand(s)
    // - does not link to types.ModifierOption['id']
    // when "command palette" has an active selection
    const modifierOption = model.options.find(({ cmdId }) => {
      // if commandId is not available, default to first modifier option
      if (!commandId) return true;
      // otherwise try and find a match
      else return cmdId === commandId;
    });
    // only use price differential if value is NOT zero
    return modifierOption?.price ? modifierOption.price : undefined;
  };

  return (
    <Flex
      {...rest}
      data-testid={`${sectionId}-${modifierKey}`}
      gap="sm"
    >
      <Divider variant="stubby" />
      <ModifierBreadCrumbs
        modifierPath={modifierPath}
        activeId={activeBreadCrumbId}
        updateActiveId={activeBreadCrumbIdSet}
      />
      <div className={styles.button_container}>
        {
          modifierChildren.map((child) => (
            <ModifierCardButton
              key={child.path.full}
              item={child}
              priceDiff={getPriceDifferential(child)}
              command={child.selections.last?.command}
              // command palette has active selections that have not been saved
              commandSelected={Boolean(modifiers.commandPalette.selected.tap || modifiers.commandPalette.selected.longPress)}
              // the modifier group/parent has "allowsDuplicates" information for the child
              allowsDuplicates={Boolean(child.parent?.allowsDuplicates)}
              onIncrement={modifiers.onItemIncrement}
              onDecrement={modifiers.onItemDecrement}
              onClick={onCardButtonClick}
              disabled={isSplit}
            />
          ))
        }
      </div>
    </Flex>
  );
};

export { ModifierCard, sectionId };
export type { ModifierCardProps };
